import { useState, useEffect, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { signInWithEmail, signInWithGoogle, sendPasswordReset } from "@/lib/auth";
import { describeResetError } from "@/lib/authErrors";
import { useAuth } from "@/hooks/useAuth";
import { isPlatformRole } from "@/types/firestore";
import { ClientPortalLogin } from "@/components/portal/ClientPortalLogin";

export function LoginPage({ redirectTo }: { redirectTo?: string }) {
  const navigate = useNavigate();
  const { user, role, loading, error: authError } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Redirect authenticated users to their dashboard
  useEffect(() => {
    if (!loading && user && role) {
      const dest = role === "ORG_REQUESTER" ? "/org/support" : redirectTo ?? (role && isPlatformRole(role) ? "/admin" : "/org");
      navigate(dest, { replace: true });
    }
  }, [loading, user, role, navigate, redirectTo]);

  // Surface auth context errors (e.g. no profile, suspended)
  useEffect(() => {
    if (authError) {
      setError(authError);
    }
  }, [authError]);

  async function handleEmailSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await signInWithEmail(email, password);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Sign-in failed. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleGoogleSignIn() {
    setError(null);
    setSubmitting(true);

    try {
      await signInWithGoogle();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Google sign-in failed."
      );
    } finally {
      setSubmitting(false);
    }
  }

  // Don't flash the form while redirecting
  if (!loading && user && role) {
    return null;
  }

  return (
    <ClientPortalLogin
      email={email}
      password={password}
      error={error}
      submitting={submitting}
      resetting={resetting}
      resetSent={resetSent}
      onEmailChange={setEmail}
      onPasswordChange={setPassword}
      onSubmit={handleEmailSubmit}
      onGoogleSignIn={handleGoogleSignIn}
      onPasswordReset={async () => {
        if (!email.trim()) {
          setError("Enter your email address first.");
          return;
        }
        setError(null);
        setResetSent(false);
        setResetting(true);
        try {
          await sendPasswordReset(email.trim());
          setResetSent(true);
        } catch (err) {
          setError(describeResetError(err));
        } finally {
          setResetting(false);
        }
      }}
    />
  );
}
